import { useState, useEffect } from 'react';
import Table from 'react-bootstrap/Table';
import Pagination from 'react-bootstrap/Pagination';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { BsPencilSquare, BsFillTrashFill } from 'react-icons/bs';
import { $get, $put, $delete } from './store/api';

const TEXT_FIELDS = [
  { id: 'SzovegPoz',        label: 'Szöveg Pozitív' },
  { id: 'SzovegNeg',        label: 'Szöveg Negatív' },
  { id: 'TablaPoz',         label: 'Tábla Pozitív' },
  { id: 'TablaNeg',         label: 'Tábla Negatív' },
];

const CEGES_FIELDS = [
  { id: 'SzovegPozCeges',   label: 'Szöveg Pozitív Céges' },
  { id: 'SzovegNegCeges',   label: 'Szöveg Negatív Céges' },
  { id: 'TablaPozCeges',    label: 'Tábla Pozitív Céges' },
  { id: 'TablaNegCeges',    label: 'Tábla Negatív Céges' },
];

function Szovegek() {
  const [szovegek, setSzovegek] = useState({});
  const [page, setPage] = useState(1);
  const [filterNyelv, setFilterNyelv] = useState('');
  const [filterKulcs, setFilterKulcs] = useState('');
  const [modal, setModal] = useState(null); // null = closed, {} = new, {id,...} = edit

  const load = async () => {
    const res = await $get('szovegek', {
      page,
      ...(filterNyelv && { Nyelv: filterNyelv }),
      ...(filterKulcs && { Kulcs: filterKulcs }),
    });
    setSzovegek(res?.data || {});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, filterNyelv, filterKulcs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allFields = ['Kulcs', 'Nyelv', ...TEXT_FIELDS.map(f => f.id), ...CEGES_FIELDS.map(f => f.id)];
    const obj = {};
    allFields.forEach(f => {
      const el = document.getElementById(`modal-${f}`);
      if (el) obj[f] = el.value;
    });
    if (modal?.id) {
      await $put('szovegek/' + modal.id, obj);
    } else {
      await $put('szovegek/', obj);
    }
    await load();
    setModal(null);
  };

  const handleDelete = async (szoveg) => {
    if (!window.confirm(`Biztosan törlöd a(z) "${szoveg.Kulcs}" kulcsú szöveget?`)) return;
    await $delete('szovegek/' + szoveg.id);
    await load();
  };

  const paginationItems = [];
  if (page > 1) paginationItems.push(<Pagination.Prev key="prev" onClick={() => setPage(p => p - 1)} />);
  paginationItems.push(<Pagination.Item key={page} active>{page}</Pagination.Item>);
  if (szovegek?.meta?.next_page) paginationItems.push(<Pagination.Next key="next" onClick={() => setPage(p => p + 1)} />);

  return (
    <>
      <div className="page-card">
        <div className="page-card-header">
          <h2>Szövegek</h2>
          <Button size="sm" variant="primary" onClick={() => setModal({})}>
            + Új szöveg
          </Button>
        </div>

        <div className="toolbar">
          <Form.Control
            size="sm"
            placeholder="Szűrés kulcsra…"
            value={filterKulcs}
            onChange={e => { setFilterKulcs(e.target.value); setPage(1); }}
          />
          <Form.Select
            size="sm"
            style={{ maxWidth: 140 }}
            value={filterNyelv}
            onChange={e => { setFilterNyelv(e.target.value); setPage(1); }}
          >
            <option value="">Minden nyelv</option>
            <option value="HU">Magyar</option>
            <option value="EN">Angol</option>
          </Form.Select>
        </div>

        <Table className="admin-table" hover responsive>
          <thead>
            <tr>
              <th>Kulcs</th>
              <th>Nyelv</th>
              <th>Cím</th>
              <th style={{ width: 80 }}>Műveletek</th>
            </tr>
          </thead>
          <tbody>
            {(szovegek?.result || []).length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-muted py-5">
                  Nincsenek találatok
                </td>
              </tr>
            ) : (
              szovegek.result.map(sz => (
                <tr key={sz.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>{sz.Kulcs}</td>
                  <td>
                    <span className={`badge ${sz.Nyelv === 'HU' ? 'bg-success' : 'bg-primary'}`}>
                      {sz.Nyelv}
                    </span>
                  </td>
                  <td className="text-muted" style={{ fontSize: 13 }}>{sz.Cim}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <BsPencilSquare
                        className="action-icon"
                        onClick={() => setModal(sz)}
                        title="Szerkesztés"
                      />
                      <BsFillTrashFill
                        className="action-icon danger"
                        onClick={() => handleDelete(sz)}
                        title="Törlés"
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <div className="table-footer">
          <Pagination size="sm" className="mb-0">{paginationItems}</Pagination>
        </div>
      </div>

      {/* Edit / Create modal */}
      <Modal show={modal !== null} onHide={() => setModal(null)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{modal?.id ? 'Szöveg szerkesztése' : 'Új szöveg létrehozása'}</Modal.Title>
        </Modal.Header>
        <Modal.Body key={modal?.id ?? 'new'}>
          <Form onSubmit={handleSubmit} id="szoveg-form">
            {/* Meta */}
            <div className="row g-3 mb-3">
              <div className="col-8">
                <Form.Label>Kulcs</Form.Label>
                <Form.Control id="modal-Kulcs" defaultValue={modal?.Kulcs} placeholder="pl. IND_D" />
              </div>
              <div className="col-4">
                <Form.Label>Nyelv</Form.Label>
                <Form.Select id="modal-Nyelv" defaultValue={modal?.Nyelv || 'HU'}>
                  <option value="HU">Magyar (HU)</option>
                  <option value="EN">Angol (EN)</option>
                </Form.Select>
              </div>
            </div>

            {/* Standard fields */}
            <p className="modal-section-title">Egyéni szövegek</p>
            {TEXT_FIELDS.map(({ id, label }) => (
              <Form.Group className="mb-3" key={id}>
                <Form.Label>{label}</Form.Label>
                <Form.Control
                  as="textarea"
                  id={`modal-${id}`}
                  rows={4}
                  defaultValue={modal?.[id]?.replace(/\\n/g, '\n')}
                />
              </Form.Group>
            ))}

            {/* Corporate fields */}
            <p className="modal-section-title">Céges szövegek</p>
            {CEGES_FIELDS.map(({ id, label }) => (
              <Form.Group className="mb-3" key={id}>
                <Form.Label>{label}</Form.Label>
                <Form.Control
                  as="textarea"
                  id={`modal-${id}`}
                  rows={4}
                  defaultValue={modal?.[id]?.replace(/\\n/g, '\n')}
                />
              </Form.Group>
            ))}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModal(null)}>Mégse</Button>
          <Button variant="primary" type="submit" form="szoveg-form">Mentés</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Szovegek;
